/* @flow */
// Music zones (leisure centres): one Pro account, several areas each playing
// its own channel. PREVIEW ONLY: switched on with ?zones=1 and kept in this
// browser's storage. The real version stores zones on the music service and
// gives each zone its own play session (see the multi-zone build plan).
import { observable, computed, decorate } from 'mobx'

const FLAG_KEY = 'gr_zones_flag'
const ZONES_KEY = 'gr_zones_preview'
const DEVICE_KEY = 'gr_zone_device'
const LINKS_KEY = 'gr_zone_setup_links'
const PLAY_ONLY_KEY = 'gr_zone_play_only'
const LINK_HOURS = 24
export const MANAGING = 'managing' // this device manages zones but plays none

// Sliding scale: first zone is Pro, each further zone 20% less, floor from zone 4
const PRICES = {
  EUR: { sign: '€', list: [69, 55, 41, 29] },
  GBP: { sign: '£', list: [59, 47, 35, 29] },
  USD: { sign: '$', list: [79, 63, 47, 35] }
}

// Channels a zone can play; Spa is music only (no ads, announcements or jingles)
export const ZONE_CHANNELS = [
  // ids are the station ids in Station.js (some differ from the names)
  { id: 'gym', name: 'Gym' },
  { id: 'fitness', name: 'Cardio' },
  { id: 'hardcore', name: 'Resistance' },
  { id: 'fight', name: 'Fight Club' },
  { id: 'cardio', name: 'Throwbacks' },
  { id: 'new-releases', name: 'New releases' },
  { id: 'lite', name: 'Lite' },
  { id: 'spa', name: 'Spa', musicOnly: true }
]

const read = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch (e) { return fallback }
}
const write = (key, value) => {
  try { window.localStorage.setItem(key, JSON.stringify(value)) } catch (e) {}
}

const newToken = () => Math.random().toString(36).slice(2, 7) + Date.now().toString(36).slice(-5)

const newCode = (taken) => {
  let code
  do {
    code = String(Math.floor(100000 + Math.random() * 900000))
  } while (taken.indexOf(code) !== -1)
  return code
}

const STARTER = () => {
  const codes = []
  const make = (name, channel, ads) => {
    const code = newCode(codes)
    codes.push(code)
    return { id: 'z' + code, name, channel, ads, code }
  }
  return [
    make('Gym floor', 'gym', true),
    make('Reception & lockers', 'lite', true),
    make('Spa & pool', 'spa', false)
  ]
}

export const isMusicOnly = (channelId) => {
  const c = ZONE_CHANNELS.find(ch => ch.id === channelId)
  return !!(c && c.musicOnly)
}

export const channelName = (channelId) => {
  const c = ZONE_CHANNELS.find(ch => ch.id === channelId)
  return c ? c.name : channelId
}

class ZonesStore {
  enabled = false
  zones = []
  deviceZoneId = ''
  screen = ''       // '' (closed) | picker | manage | setup
  setupZoneId = ''  // zone the setup link is for
  setupUrl = ''
  setupExpires = 0
  justSetUp = null  // zone this device was just set up for by a link
  linkError = ''
  currency = 'EUR'
  playOnly = false      // this device plays its zone but can't manage anything
  speakerOk = false     // manager said yes to previewing through the speakers
  speakerPending = null // the preview waiting for that answer

  constructor () {
    try {
      const params = new window.URLSearchParams(window.location.search)
      if (params.get('zones') === '1') {
        write(FLAG_KEY, true)
        // preview only: ?zones=1 also undoes play-only (the real way is the manager signing in again)
        try { window.localStorage.removeItem(PLAY_ONLY_KEY) } catch (e) {}
      }
      if (params.get('zones') === '0') {
        try { window.localStorage.removeItem(FLAG_KEY) } catch (e) {}
      }
    } catch (e) {}
    this.enabled = !!read(FLAG_KEY, false)
    if (!this.enabled) return
    this.zones = read(ZONES_KEY, null) || STARTER()
    write(ZONES_KEY, this.zones)
    this.deviceZoneId = read(DEVICE_KEY, '')
    if (!this.deviceZone && this.deviceZoneId !== MANAGING) this.deviceZoneId = ''
    this.playOnly = !!read(PLAY_ONLY_KEY, false) && !!this.deviceZone
    this.redeemSetupLink()
  }

  // A setup link (?setup=token) puts this device in its zone, no password needed.
  // PREVIEW: links are kept in this browser, so they only work in the same browser.
  // The real link is checked by the music service and signs the device in to that
  // zone only (no billing, no other zones).
  redeemSetupLink () {
    let token = ''
    try {
      const url = new window.URL(window.location.href)
      token = url.searchParams.get('setup') || ''
      if (!token) return
      url.searchParams.delete('setup')
      window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    } catch (e) { return }
    const links = read(LINKS_KEY, {})
    const link = links[token]
    const zone = link && this.zones.find(z => z.id === link.zoneId)
    if (!link || link.used || link.expires < Date.now() || !zone) {
      this.linkError = 'This setup link has expired or was already used. Ask your manager for a new one.'
      this.screen = 'linkError'
      return
    }
    link.used = true
    write(LINKS_KEY, links)
    this.deviceZoneId = zone.id
    write(DEVICE_KEY, zone.id)
    this.justSetUp = zone
  }

  get isManaging () {
    return this.deviceZoneId === MANAGING
  }

  get deviceZone () {
    return this.zones.find(z => z.id === this.deviceZoneId) || null
  }

  // Price of each zone in order, and the monthly total
  get priced () {
    const p = PRICES[this.currency] || PRICES.EUR
    return this.zones.map((z, i) => ({
      zone: z,
      price: p.list[Math.min(i, p.list.length - 1)]
    }))
  }

  get total () {
    return this.priced.reduce((sum, row) => sum + row.price, 0)
  }

  get floor () {
    const list = (PRICES[this.currency] || PRICES.EUR).list
    return list[list.length - 1]
  }

  get sign () {
    return (PRICES[this.currency] || PRICES.EUR).sign
  }

  save () {
    write(ZONES_KEY, this.zones)
  }

  // Called once the gym is signed in: ask which area this device plays in
  askIfNeeded (isAuthenticated) {
    if (this.enabled && isAuthenticated && !this.deviceZone && !this.isManaging && !this.screen) {
      this.screen = 'picker'
    }
  }

  open (screen) {
    this.screen = screen
  }

  close () {
    this.screen = ''
    this.linkError = ''
    this.speakerPending = null
  }

  chooseZone (id) {
    this.deviceZoneId = id
    write(DEVICE_KEY, id)
    this.close()
  }

  // Ad Studio previews on a device that plays a zone come out of that area's
  // speakers: returns true (and asks) when the preview should wait for an answer
  speakerCheck (play) {
    if (!this.enabled || !this.deviceZone || this.speakerOk) return false
    this.speakerPending = play
    this.screen = 'speaker'
    return true
  }

  speakerAnswer (playHere) {
    const play = this.speakerPending
    this.speakerPending = null
    this.close()
    if (playHere) {
      this.speakerOk = true
      if (play) play()
    }
  }

  // Lock a signed-in device so it only plays its zone (for devices staff can reach)
  makePlayOnly () {
    if (!this.deviceZone) return
    this.playOnly = true
    write(PLAY_ONLY_KEY, true)
    this.close()
  }

  // "Set up a device": a one-time link (and QR code) for one zone, valid 24 hours
  makeSetupLink (zoneId) {
    const links = read(LINKS_KEY, {})
    const now = Date.now()
    Object.keys(links).forEach(t => { if (links[t].expires < now) delete links[t] })
    const token = newToken()
    const expires = now + LINK_HOURS * 3600 * 1000
    links[token] = { zoneId, expires, used: false }
    write(LINKS_KEY, links)
    this.setupZoneId = zoneId
    this.setupUrl = window.location.origin + '/?setup=' + token
    this.setupExpires = expires
    this.screen = 'setup'
  }

  get setupZone () {
    return this.zones.find(z => z.id === this.setupZoneId) || null
  }

  addZone () {
    const code = newCode(this.zones.map(z => z.code))
    this.zones.push({ id: 'z' + code, name: 'Zone ' + (this.zones.length + 1), channel: 'lite', ads: true, code })
    this.save()
  }

  updateZone (id, changes) {
    const zone = this.zones.find(z => z.id === id)
    if (!zone) return
    Object.assign(zone, changes)
    if (isMusicOnly(zone.channel)) zone.ads = false
    this.save()
  }

  removeZone (id) {
    if (this.zones.length <= 1) return
    this.zones = this.zones.filter(z => z.id !== id)
    if (this.deviceZoneId === id) {
      this.deviceZoneId = ''
      write(DEVICE_KEY, '')
    }
    this.save()
  }

}

decorate(ZonesStore, {
  zones: observable,
  deviceZoneId: observable,
  screen: observable,
  setupZoneId: observable,
  setupUrl: observable,
  setupExpires: observable,
  justSetUp: observable,
  linkError: observable,
  playOnly: observable,
  speakerOk: observable,
  currency: observable,
  deviceZone: computed,
  isManaging: computed,
  setupZone: computed,
  priced: computed,
  total: computed,
  sign: computed,
  floor: computed
})

// One shared instance, so the header menu and the player use the same zones
const zones = new ZonesStore()
export default zones
